import { gql } from '@do/seashell/graphql';
import { trackEvent } from '@do/seashell';
import { LoadingState } from '@do/walrus';
import _get from 'lodash/get';
import { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import AppDetail from '../../components/AppDetail';
import { getTrackingPrefix } from '../../components/AppDetail/AppDetail';
import MetaTags from '../../components/MetaTags';
import { HitProps } from '../../components/Search/onSearchSelect';
import GetAppBySafeNameQuery from '../../graphql/queries/getAppBySafeName.gql';
import { PrismicRef } from '../../interfaces/Prismic';
import Main from '../../layouts/Main';
import { getIcon, getName, getSummary, getType } from '../../selectors';
import { ALGOLIA_TRACK_VIEW_EVENT } from '../../utils/constants';
import getCreateButtonUrl from '../../utils/getCreateButtonUrl';
import initApollo from '../../utils/init-apollo';
import IsomorphicSentry from '../../utils/sentry';
import {
  trackGTMEvent,
  trackPage,
  trackProductSearch
} from '../../utils/tracking';
import { AppType } from '../../graphql/generated-types/globalTypes';

const sentryInstance = IsomorphicSentry();

const { publicRuntimeConfig } = getConfig();
const { SITE_URL } = publicRuntimeConfig;

const OLD_START_FRAGMENT = '#start';
const NEW_START_FRAGMENT = '#getting-started';

const SAFELISTED_APP_SAFE_NAMES = [
  'wordpress',
  'docker',
  'nodejs',
  'lamp',
  'lemp'
];

export const is4xxError = (statusCode: number) =>
  statusCode >= 400 && statusCode <= 499;

export const isSafelistedAppSafeName = (safeName: string) =>
  SAFELISTED_APP_SAFE_NAMES.indexOf(safeName) > -1;

interface Props {
  safeName: string;
  app: any;
  preview?: boolean;
  previewData?: PrismicRef;
}

const Page = ({ safeName, app, preview, previewData }: Props) => {
  const router = useRouter();
  const [isInIframe, setIsInIframe] = useState(false);
  const [hit, setHit] = useState({} as HitProps);

  useEffect(() => {
    if (router?.query?.iframe === 'true') {
      setIsInIframe(true);
    }
  }, [router?.query?.iframe]);

  useEffect(() => {
    if (
      router?.query?.objectId &&
      router?.query?.queryId &&
      router?.query?.position
    ) {
      const hitQuery: HitProps = {
        objectId: router?.query?.objectId as string,
        queryId: router?.query?.queryId as string,
        position: parseInt(router?.query?.position as string)
      };

      setHit(hitQuery);
      trackProductSearch(ALGOLIA_TRACK_VIEW_EVENT, 'view', hitQuery);
    }
  }, [
    router?.query?.objectId,
    router?.query?.queryId,
    router?.query?.position
  ]);

  const trackCreateButton = ({ developer, type: appType, resource }) => {
    trackEvent('Click Create App', {
      vendor_name: safeName,
      developer,
      type: appType,
      resource,
      marketplace_app_id: _get(app, 'appId')
    });
  };

  useEffect(() => {
    trackPage('App Detail', {
      vendor_name: safeName,
      marketplace_app_id: _get(app, 'appId')
    });

    if (router.asPath.includes(OLD_START_FRAGMENT)) {
      const path = router.asPath.replace(
        OLD_START_FRAGMENT,
        NEW_START_FRAGMENT
      );
      router.replace(path);
    }
  }, []);

  if (router?.isFallback) {
    return <LoadingState sammy={true} className="do-u-mt--xlarge" />;
  }

  const createButtonUrl = getCreateButtonUrl(app);
  const name = getName(app);
  const summary = getSummary(app);
  const icon = getIcon(app);
  const type = getType(app);

  const pageProps = {
    action: 'click',
    name: `${type} app detail page`,
    pageName: `${type} app detail`
  };

  return (
    <>
      <MetaTags
        title={`${name} | DigitalOcean Marketplace 1-Click App`}
        url={`${SITE_URL}/apps/${safeName}`}
        description={summary}
        imageUrl={icon}
        imageAlt={name}
      />

      <Main
        preview={preview}
        prismicRef={previewData?.ref}
        isInIframe={isInIframe}
      >
        <Main.Page>
          <AppDetail
            app={app}
            createButtonUrl={createButtonUrl}
            onClickCreateButton={() => {
              trackCreateButton.bind(this);
              trackGTMEvent(getTrackingPrefix(type), 'finish', {
                ...pageProps,
                action: 'click'
              });
            }}
            showSearch={true}
            isInIframe={isInIframe}
            hit={hit ? hit : undefined}
          />
        </Main.Page>
      </Main>
    </>
  );
};

// This is called at build time and prerenders all pages to HTML
export const getServerSideProps: GetServerSideProps<
  any,
  any,
  { ref: string }
> = async ({ params, preview = false, previewData = {} }) => {
  const { safeName } = params;
  const apolloClient = initApollo({});

  try {
    const { data, error } = await apolloClient.query({
      query: gql`
        ${GetAppBySafeNameQuery}
      `,
      variables: {
        payload: {
          safe_name: safeName,
          filtering_types: [
            AppType.NONE,
            AppType.DROPLET,
            AppType.KUBERNETES,
            AppType.SAAS_AFFILIATE,
            AppType.AI_MODEL
          ]
        }
      }
    });

    if (
      typeof safeName === 'string' &&
      isSafelistedAppSafeName(safeName) &&
      is4xxError(error?.status)
    ) {
      sentryInstance.captureException(
        `Unexpected 4xx error for known Marketplace app page for '${safeName}'`
      );
    }

    const app = _get(data, 'getAppBySafeName.app');

    if (!app) {
      return {
        notFound: true
      };
    }

    return {
      props: {
        preview,
        previewData,
        initialApolloState: apolloClient.cache.extract(),
        safeName,
        app
      }
    };
  } catch (error) {
    // All other ApolloErrors need to be caught
    return {
      notFound: true
    };
  }
};

export default Page;
